import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterPanel = ({ filters, onFiltersChange, isOpen, onToggle, resultCount }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'mindfulness-meditation', label: 'Mindfulness & Meditation' },
    { value: 'yoga-movement', label: 'Yoga & Movement' },
    { value: 'energy-healing', label: 'Energy Healing' },
    { value: 'complementary-therapies', label: 'Complementary Therapies' },
    { value: 'nutrition-wellness', label: 'Nutrition & Wellness' },
    { value: 'spirituality-esoterica', label: 'Spirituality & Esoterica' },
    { value: 'sound-breathwork', label: 'Sound & Breathwork' },
    { value: 'coaching-development', label: 'Coaching & Development' },
    { value: 'creative-therapy', label: 'Creative Therapy' },
    { value: 'retreats-immersions', label: 'Retreats & Immersions' },
    { value: 'nature-eco-therapy', label: 'Nature & Eco Therapy' },
    { value: 'ceremonies-community', label: 'Ceremonies & Community' },
    { value: 'training-professional', label: 'Training & Professional' },
    { value: 'courses', label: 'Courses' },
    { value: 'circles', label: 'Circles' }
  ];

  const sortOptions = [
    { value: 'relevance', label: 'Most Relevant' },
    { value: 'distance', label: 'Nearest First' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'date-newest', label: 'Newest First' },
    { value: 'date-oldest', label: 'Oldest First' }
  ];

  const priceRanges = [
    { value: 'all', label: 'Any Price' },
    { value: '0-25', label: 'Under £25' },
    { value: '25-50', label: '£25 - £50' },
    { value: '50-100', label: '£50 - £100' },
    { value: '100-200', label: '£100 - £200' },
    { value: '200+', label: 'Over £200' }
  ];

  const handleFilterChange = (key, value) => {
    const updatedFilters = { ...localFilters, [key]: value };
    setLocalFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      category: 'all',
      location: '',
      dateFrom: '',
      dateTo: '',
      priceRange: 'all',
      deliveryType: 'all',
      sortBy: 'relevance',
      ukWide: false
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Search Query */}
      <div>
        <Input
          label="Search"
          type="search"
          placeholder="Search practitioners, events, or services..."
          value={localFilters?.query || ''}
          onChange={(e) => handleFilterChange('query', e?.target?.value)}
          className="w-full"
        />
      </div>

      {/* Category Filter */}
      <div>
        <Select
          label="Category"
          options={categories}
          value={localFilters?.category}
          onChange={(value) => handleFilterChange('category', value)}
          placeholder="Select category"
          error=""
        />
      </div>

      {/* Location Filter */}
      <div>
        <Input
          label="Location"
          type="text"
          placeholder="Enter postcode or city"
          value={localFilters?.location}
          onChange={(e) => handleFilterChange('location', e?.target?.value)}
          description="e.g. SW1A 1AA or London"
        />
        <div className="mt-2">
          <Checkbox
            label="Search UK-wide"
            checked={localFilters?.ukWide}
            onChange={(e) => handleFilterChange('ukWide', e?.target?.checked)}
            description="Expand search beyond local area"
          />
        </div>
      </div>

      {/* Date Range */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="From Date"
          type="date"
          value={localFilters?.dateFrom}
          onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
        />
        <Input
          label="To Date"
          type="date"
          value={localFilters?.dateTo}
          onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
        />
      </div>

      {/* Price Range */}
      <div>
        <Select
          label="Price Range"
          options={priceRanges}
          value={localFilters?.priceRange}
          onChange={(value) => handleFilterChange('priceRange', value)}
          error=""
        />
      </div>

      {/* Delivery Type */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-3">
          Delivery Type
        </label>
        <div className="space-y-2">
          <Checkbox
            label="In-person sessions"
            checked={localFilters?.deliveryType === 'all' || localFilters?.deliveryType === 'in-person'}
            onChange={(e) => {
              if (e?.target?.checked) {
                handleFilterChange('deliveryType', localFilters?.deliveryType === 'online' ? 'all' : 'in-person');
              } else {
                handleFilterChange('deliveryType', localFilters?.deliveryType === 'all' ? 'online' : 'all');
              }
            }}
          />
          <Checkbox
            label="Online sessions"
            checked={localFilters?.deliveryType === 'all' || localFilters?.deliveryType === 'online'}
            onChange={(e) => {
              if (e?.target?.checked) {
                handleFilterChange('deliveryType', localFilters?.deliveryType === 'in-person' ? 'all' : 'online');
              } else {
                handleFilterChange('deliveryType', localFilters?.deliveryType === 'all' ? 'in-person' : 'all');
              }
            }}
          />
        </div>
      </div>

      {/* Sort By */}
      <div>
        <Select
          label="Sort By"
          options={sortOptions}
          value={localFilters?.sortBy}
          onChange={(value) => handleFilterChange('sortBy', value)}
          error=""
        />
      </div>

      {/* Clear Filters */}
      <div className="pt-4 border-t border-border">
        <Button
          variant="outline"
          onClick={handleClearFilters}
          iconName="RotateCcw"
          iconPosition="left"
          fullWidth
        >
          Clear All Filters
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Filter Panel */}
      <div className="hidden lg:block bg-card rounded-lg p-6 shadow-organic">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-heading font-semibold text-foreground">
            Filters
          </h2>
          <div className="text-sm text-muted-foreground">
            {resultCount} results
          </div>
        </div>
        <FilterContent />
      </div>

      {/* Mobile Filter Button */}
      <div className="lg:hidden">
        <Button
          variant="outline"
          onClick={onToggle}
          iconName="Filter"
          iconPosition="left"
          fullWidth
        >
          Filters ({resultCount} results)
        </Button>
      </div>

      {/* Mobile Filter Overlay */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onToggle}
          />
          <div className="relative ml-auto w-full max-w-sm bg-background h-full overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-heading font-semibold text-foreground">
                  Filters
                </h2>
                <button
                  onClick={onToggle}
                  className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-gentle"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
              <FilterContent />
              <div className="mt-6 pt-6 border-t border-border">
                <Button
                  variant="default"
                  onClick={onToggle}
                  fullWidth
                >
                  Apply Filters
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FilterPanel;